import React, { useState, useEffect, useRef } from 'react';
import CompanySettingsService, { CompanySettings } from '../../../services/CompanySettingsService';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../config/firebase';
import { doc, updateDoc } from 'firebase/firestore';

const Practice: React.FC = () => {
  const { user } = useAuth();
  const companySettingsService = CompanySettingsService.getInstance();
  const [settings, setSettings] = useState<CompanySettings>(companySettingsService.getSettings());
  const [logoPreview, setLogoPreview] = useState<string | undefined>(settings.companyLogo);
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>(settings.phone ? [settings.phone] : []);
  const [locations, setLocations] = useState<any[]>([]);
  const [showAddPhoneModal, setShowAddPhoneModal] = useState(false);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [ein, setEin] = useState('');
  const [einError, setEinError] = useState('');
  const einEdited = useRef(false);

  const formatEin = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
  };

  useEffect(() => {
    let cancelled = false;
    const loadEin = async () => {
      const saved = await companySettingsService.getEin();
      if (!cancelled && !einEdited.current) setEin(saved);
    };
    loadEin();
    window.addEventListener('companySettingsUpdated', loadEin);
    return () => {
      cancelled = true;
      window.removeEventListener('companySettingsUpdated', loadEin);
    };
  }, []);

  useEffect(() => {
    const updateSettings = () => {
      const newSettings = companySettingsService.getSettings();
      setSettings(newSettings);
      setLogoPreview(newSettings.companyLogo);
    };

    companySettingsService.subscribe(updateSettings);
    return () => companySettingsService.unsubscribe(updateSettings);
  }, []);

  const compressImage = (file: File, maxWidth: number = 300, quality: number = 0.7): Promise<string> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedDataUrl);
      };

      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const compressed = await compressImage(file);
        setLogoPreview(compressed);
        companySettingsService.updateCompanyLogo(compressed);
        // Also sync to Firestore so clients see it immediately
        if (user?.uid) {
          await updateDoc(doc(db, 'providers', user.uid), { practiceLogo: compressed });
        }
      } catch (error) {
        console.error('Error uploading logo:', error);
      }
    }
  };

  const handleSave = async () => {
    if (ein && ein.replace(/\D/g, '').length !== 9) {
      setEinError('EIN must be 9 digits, like 12-3456789.');
      return;
    }
    setEinError('');

    companySettingsService.updateSettings(settings);
    try {
      await companySettingsService.setEin(ein);
      einEdited.current = false;
    } catch (error) {
      console.error('Failed to save EIN:', error);
      setEinError('Could not save the EIN. Your other changes were saved.');
    }

    // Sync practice info to Firestore so clients see it in Practice Support
    if (user?.uid && settings.companyName) {
      try {
        const firestoreData: Record<string, any> = {
          practiceName: settings.companyName,
        };
        // Only include optional fields if they have actual values (Firestore rejects undefined)
        if (settings.phone) firestoreData.phone = settings.phone;
        if (settings.email) firestoreData.email = settings.email;
        if (settings.address) firestoreData.officeAddress = settings.address;
        if (settings.website) firestoreData.website = settings.website;
        // Include logo if one is set (base64 compressed)
        const currentLogo = logoPreview || companySettingsService.getSettings().companyLogo;
        if (currentLogo) {
          firestoreData.practiceLogo = currentLogo;
        }
        await updateDoc(doc(db, 'providers', user.uid), firestoreData);
      } catch (error) {
        console.error('Failed to sync practice info to Firestore:', error);
      }
    }

    setShowSaveSuccessModal(true);
  };

  const handleAddPhone = () => {
    if (newPhone.trim()) {
      setPhoneNumbers([...phoneNumbers, newPhone]);
      setSettings({ ...settings, phone: newPhone });
      setNewPhone('');
      setShowAddPhoneModal(false);
    }
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1200px' }}>
      {/* Save Button - Top Right */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
        <button
          onClick={handleSave}
          style={{
            padding: '10px 24px',
            backgroundColor: '#226F9E',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Save changes
        </button>
      </div>

      {/* Page Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 8px 0' }}>
          Practice details
        </h1>
        <p style={{ fontSize: '14px', color: '#87CEEB', margin: 0 }}>
          Practice name and location info
        </p>
      </div>

      {/* Practice Name */}
      <div style={{ marginBottom: '32px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px' }}>
          Practice Name
        </label>
        <input
          type="text"
          value={settings.companyName}
          onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: '#113F67',
            border: '1px solid #6CA6C1',
            borderRadius: '6px',
            color: '#FFFFFF',
            fontSize: '14px'
          }}
        />
      </div>

      {/* EIN */}
      <div style={{ marginBottom: '32px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px' }}>
          EIN (Employer Identification Number)
        </label>
        <input
          type="text"
          inputMode="numeric"
          placeholder="12-3456789"
          value={ein}
          onChange={(e) => { einEdited.current = true; setEin(formatEin(e.target.value)); }}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: '#113F67',
            border: '1px solid #6CA6C1',
            borderRadius: '6px',
            color: '#FFFFFF',
            fontSize: '14px'
          }}
        />
        {einError && (
          <p style={{ fontSize: '12px', color: '#FCA5A5', marginTop: '8px' }}>{einError}</p>
        )}
        <p style={{ fontSize: '12px', color: '#87CEEB', marginTop: '8px' }}>
          Stored privately. Clients cannot see it.
        </p>
      </div>

      {/* Time Zone */}
      <div style={{ marginBottom: '32px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px' }}>
          Time Zone
        </label>
        <select
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: '#113F67',
            border: '1px solid #6CA6C1',
            borderRadius: '6px',
            color: '#FFFFFF',
            fontSize: '14px'
          }}
        >
          <option>Eastern Time (US & Canada) UTC-04:00</option>
          <option>Central Time (US & Canada) UTC-05:00</option>
          <option>Mountain Time (US & Canada) UTC-06:00</option>
          <option>Pacific Time (US & Canada) UTC-07:00</option>
        </select>
        <p style={{ fontSize: '12px', color: '#87CEEB', marginTop: '8px' }}>
          Applies to data exports, activity logs, and analytic reports.
        </p>
      </div>

      {/* Practice Logo */}
      <div style={{ marginBottom: '32px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px' }}>
          Practice Logo
        </label>
        <p style={{ fontSize: '12px', color: '#87CEEB', marginBottom: '16px' }}>
          Add your practice's logo for use on your MyClinicCore documents. You can manage which documents include the logo here
        </p>

        <div style={{
          padding: '32px',
          backgroundColor: '#113F67',
          border: '2px dashed #6CA6C1',
          borderRadius: '8px',
          textAlign: 'center',
          position: 'relative'
        }}>
          {logoPreview ? (
            <div>
              <img
                src={logoPreview}
                alt="Practice Logo"
                style={{
                  maxWidth: '200px',
                  maxHeight: '200px',
                  marginBottom: '16px',
                  borderRadius: '8px'
                }}
              />
              <div>
                <label style={{
                  padding: '8px 16px',
                  backgroundColor: '#226F9E',
                  color: '#FFFFFF',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginRight: '8px'
                }}>
                  Change image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    style={{ display: 'none' }}
                  />
                </label>
                <button
                  onClick={async () => {
                    setLogoPreview(undefined);
                    companySettingsService.updateCompanyLogo('');
                    // Also remove from Firestore
                    if (user?.uid) {
                      try {
                        await updateDoc(doc(db, 'providers', user.uid), { practiceLogo: '' });
                      } catch (err) { /* non-critical */ }
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: '#87CEEB',
                    border: '1px solid #6CA6C1',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '48px', color: '#6CA6C1', marginBottom: '16px' }}>☁️</div>
              <label style={{
                padding: '10px 20px',
                backgroundColor: '#226F9E',
                color: '#FFFFFF',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600
              }}>
                Choose image
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                />
              </label>
              <p style={{ fontSize: '12px', color: '#87CEEB', marginTop: '8px' }}>
                or drag and drop image
              </p>
              <p style={{ fontSize: '11px', color: '#6CA6C1', marginTop: '4px' }}>
                Upload a .jpg or .png image under 10 MB, with a minimum of 200px height and 300px width.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Practice Phone */}
      <div style={{ marginBottom: '32px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px' }}>
          Practice Phone
        </label>
        {phoneNumbers.length > 0 ? (
          <div>
            {phoneNumbers.map((phone, index) => (
              <div key={index} style={{
                padding: '12px',
                backgroundColor: '#113F67',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                marginBottom: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ color: '#FFFFFF' }}>{phone}</span>
                <button
                  onClick={() => setPhoneNumbers(phoneNumbers.filter((_, i) => i !== index))}
                  style={{
                    padding: '4px 12px',
                    backgroundColor: 'transparent',
                    color: '#FF6B6B',
                    border: '1px solid #FF6B6B',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <button
          onClick={() => setShowAddPhoneModal(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: 'transparent',
            color: '#87CEEB',
            border: '1px solid #6CA6C1',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          Add Phone Number
        </button>
      </div>

      {/* Office Location */}
      <div style={{ marginBottom: '32px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px' }}>
          Office location
        </label>
        <p style={{ fontSize: '12px', color: '#87CEEB', marginBottom: '16px' }}>
          By default, each office address is hidden from public view. Office addresses will always be visible to clients who sign in to the Client Portal and receive appointment reminders.
        </p>
        <button
          onClick={() => setShowAddLocationModal(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: 'transparent',
            color: '#87CEEB',
            border: '1px solid #6CA6C1',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          Add a location
        </button>
      </div>

      {/* Telehealth */}
      <div style={{ marginBottom: '32px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px' }}>
          Telehealth
        </label>
        <p style={{ fontSize: '12px', color: '#87CEEB', marginBottom: '16px' }}>
          Your video appointments are conducted through the Telehealth feature. You and your clients will get a unique link to access these appointments.
        </p>

        {/* Telehealth Table */}
        <div style={{
          backgroundColor: '#113F67',
          border: '1px solid #6CA6C1',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#0A2540' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>Address</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>Color</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>Place Of Service</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>Public View</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}></th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderTop: '1px solid #6CA6C1' }}>
                <td style={{ padding: '12px', color: '#FFFFFF', fontSize: '14px' }}>Video Office 🛈</td>
                <td style={{ padding: '12px', color: '#87CEEB', fontSize: '14px', fontStyle: 'italic' }}>No address added</td>
                <td style={{ padding: '12px' }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#9B59B6' }}></div>
                </td>
                <td style={{ padding: '12px', color: '#FFFFFF', fontSize: '14px' }}>02-Telehealth Provided Other than in Patient's Home</td>
                <td style={{ padding: '12px', color: '#FFFFFF', fontSize: '14px' }}>Hidden 🛈</td>
                <td style={{ padding: '12px' }}>
                  <button style={{
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    color: '#87CEEB',
                    border: '1px solid #6CA6C1',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}>
                    Edit
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button
          style={{
            padding: '8px 16px',
            backgroundColor: 'transparent',
            color: '#FF6B6B',
            border: '1px solid #FF6B6B',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            marginTop: '12px'
          }}
        >
          Turn off
        </button>
      </div>

      {/* Billing Addresses */}
      <div style={{ marginBottom: '32px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px' }}>
          Billing addresses
        </label>
        <p style={{ fontSize: '12px', color: '#87CEEB', marginBottom: '16px' }}>
          Your business billing address will be displayed on your MyClinicCore subscription invoices. The client billing address will be displayed on your insurance claims and client-facing billing documents such as invoices, statements, and superbills.
        </p>

        {/* Billing Addresses Table */}
        <div style={{
          backgroundColor: '#113F67',
          border: '1px solid #6CA6C1',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#0A2540' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>Address / Office</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}></th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderTop: '1px solid #6CA6C1' }}>
                <td style={{ padding: '12px', color: '#FFFFFF', fontSize: '14px' }}>Business Billing</td>
                <td style={{ padding: '12px', color: '#FFFFFF', fontSize: '14px' }}>
                  626 Main Street, Palmetto, GA, 30268-1141
                </td>
                <td style={{ padding: '12px' }}>
                  <button style={{
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    color: '#87CEEB',
                    border: '1px solid #6CA6C1',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}>
                    Edit
                  </button>
                </td>
              </tr>
              <tr style={{ borderTop: '1px solid #6CA6C1' }}>
                <td style={{ padding: '12px', color: '#FFFFFF', fontSize: '14px' }}>Client Billing 🛈</td>
                <td style={{ padding: '12px', color: '#87CEEB', fontSize: '14px', fontStyle: 'italic' }}>No address added</td>
                <td style={{ padding: '12px' }}>
                  <button style={{
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    color: '#87CEEB',
                    border: '1px solid #6CA6C1',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}>
                    Add
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Phone Modal */}
      {showAddPhoneModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#0A2540',
            border: '1px solid #6CA6C1',
            borderRadius: '8px',
            padding: '24px',
            width: '400px'
          }}>
            <h3 style={{ color: '#FFFFFF', marginBottom: '16px' }}>Add Phone Number</h3>
            <input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="Enter phone number"
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#113F67',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                marginBottom: '16px'
              }}
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAddPhoneModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  color: '#87CEEB',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddPhone}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#226F9E',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Success Modal */}
      {showSaveSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#0A2540',
            border: '1px solid #6CA6C1',
            borderRadius: '12px',
            padding: '32px',
            width: '400px',
            textAlign: 'center'
          }}>
            <h2 style={{
              color: '#FFFFFF',
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: 600
            }}>
              Practice details saved successfully!
            </h2>
            <button
              onClick={() => setShowSaveSuccessModal(false)}
              style={{
                padding: '10px 32px',
                backgroundColor: '#226F9E',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '24px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                marginTop: '16px'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Practice;
